import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown, Input, Menu, Modal, Popconfirm, Space, Switch, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { ListFilter } from 'lucide-react';
import { TbDeviceFloppy, TbEdit, TbLock, TbTrash, TbWorld } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    SavedFilter,
    SavedFilterResourceType,
    SavedFilterScope,
    savedFilterService,
    SavedFilterVisibility,
} from '@/services';
import {
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    getAdvancedFilterCounts,
    LearnMoreFooter,
    SmallTooltip,
    type AdvancedFilterDefinition,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { PlatformForm as Form, SettingsToggleRow } from '@/shared/components/forms';
import { ListItemSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { ListType } from '@/types';
import { ellipsisStyling } from '@/utilities';

import {
    buildListFilterJsonLogic,
    canReuseListFilterValue,
    FilterableColumn,
    getOperatorOptions,
    ListFilterRule,
    normalizeListFilterValues,
} from '../01-FilterDropdown';

interface SavedFiltersDropdownProps {
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule>;
    canManageSharedFilters?: boolean;
    columns: FilterableColumn[];
    compatibleResourceLabel?: string;
    docsLabel?: string;
    filters: ListFilterRule[];
    resourceId?: string;
    resourceLabel?: string;
    resourceType?: SavedFilterResourceType;
    resourceScopedLabel?: string;
    targetKind?: ListType;
    workspaceScopeDescription?: string;
    workspaceScopeTitle?: string;
    setFilters: (next: ListFilterRule[] | null, options?: { savedFilterId?: string | null }) => void;
    setFilterDefinition?: (
        next: ListFilterRule[] | null,
        advancedFilter: AdvancedFilterDefinition<ListFilterRule> | null,
        options?: { savedFilterId?: string | null },
    ) => void;
}

interface SavedFilterFormValues {
    description?: string;
    isResourceDefault: boolean;
    name: string;
    replaceDefinition: boolean;
    scope: SavedFilterScope;
    visibility: SavedFilterVisibility;
}

const DEFAULT_SAVED_FILTER_VALUES: SavedFilterFormValues = {
    description: '',
    isResourceDefault: false,
    name: '',
    replaceDefinition: true,
    scope: SavedFilterScope.RESOURCE,
    visibility: SavedFilterVisibility.PERSONAL,
};

const SavedFiltersDropdown: FC<SavedFiltersDropdownProps> = ({
    advancedFilter,
    canManageSharedFilters = false,
    columns,
    compatibleResourceLabel = 'Lists',
    docsLabel = 'saved List filters',
    filters,
    resourceId,
    resourceLabel = 'List',
    resourceType = SavedFilterResourceType.LIST,
    resourceScopedLabel = 'This List',
    targetKind,
    workspaceScopeDescription = 'Workspace filters can be applied anywhere the List type and columns are compatible.',
    workspaceScopeTitle = 'Available to compatible Lists',
    setFilters,
    setFilterDefinition,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [form] = Form.useForm<SavedFilterFormValues>();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedFilters, setSavedFilters] = useState<SavedFilter<ListFilterRule>[]>([]);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingFilter, setEditingFilter] = useState<SavedFilter<ListFilterRule> | null>(null);
    const selectedScope = Form.useWatch('scope', form);
    const selectedVisibility = Form.useWatch('visibility', form);
    const selectedIsResourceDefault = Form.useWatch('isResourceDefault', form) ?? false;
    const selectedReplaceDefinition = Form.useWatch('replaceDefinition', form) ?? true;

    const visibleColumns = useMemo(() => (columns ?? []).filter((column) => !column.isHidden), [columns]);

    const filteredSavedFilters = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        if (!normalizedSearch) {
            return savedFilters;
        }

        return savedFilters.filter((filter) =>
            [filter.name, filter.description, filter.targetKind, filter.scope, filter.visibility]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedSearch)),
        );
    }, [savedFilters, search]);

    const buildDefinition = () => ({
        ...(advancedFilter ? { advancedFilter } : {}),
        columns: filters.map((filter) => ({
            dataType: filter.dataType,
            key: filter.field,
            label: filter.fieldLabel,
        })),
        jsonLogic: buildListFilterJsonLogic(filters, advancedFilter),
        rules: filters,
    });

    const loadSavedFilters = useCallback(async () => {
        if (!resourceId) {
            return;
        }

        setLoading(true);

        try {
            const result = await savedFilterService.list<ListFilterRule>({
                resourceId,
                resourceType,
                targetKind,
            });

            setSavedFilters(result);
        } catch (error) {
            captureException(error, { tags: { feature: 'saved-filters-list', resourceType } });
        } finally {
            setLoading(false);
        }
    }, [resourceId, resourceType, targetKind]);

    useEffect(() => {
        void loadSavedFilters();
    }, [loadSavedFilters]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            return;
        }

        void loadSavedFilters();
    }, [loadSavedFilters, open]);

    const openCreateModal = () => {
        setEditingFilter(null);
        setModalOpen(true);
    };

    const openEditModal = (filter: SavedFilter<ListFilterRule>) => {
        setEditingFilter(filter);
        setModalOpen(true);
    };

    const getCompatibilityIssue = (filter: SavedFilter<ListFilterRule>) => {
        if (filter.compatibility?.applicable === false) {
            return filter.compatibility.disabledReason ?? 'This saved filter cannot be applied here.';
        }

        const rules = [
            ...(filter.definition?.rules ?? []),
            ...(filter.definition?.advancedFilter?.groups.flatMap((group) =>
                group.conditions.map((condition) => condition.rule),
            ) ?? []),
        ];
        const incompatibleRule = rules.find((rule) => {
            const column = visibleColumns.find((candidate) => candidate.key === rule.field);

            return !column || !canReuseListFilterValue(rule.dataType, column.dataType);
        });

        if (!incompatibleRule) {
            return null;
        }

        const column = visibleColumns.find((candidate) => candidate.key === incompatibleRule.field);

        if (!column) {
            return `${incompatibleRule.fieldLabel} is not available in this ${resourceLabel}.`;
        }

        return `${incompatibleRule.fieldLabel} no longer has a compatible data type.`;
    };

    const getUnavailableOptionIssue = (filter: SavedFilter<ListFilterRule>) => {
        const rules = [
            ...(filter.definition?.rules ?? []),
            ...(filter.definition?.advancedFilter?.groups.flatMap((group) =>
                group.conditions.map((condition) => condition.rule),
            ) ?? []),
        ];
        const ruleWithUnavailableOption = rules.find((rule) => {
            const column = visibleColumns.find((candidate) => candidate.key === rule.field);

            if (!column?.filterOptions?.length) {
                return false;
            }

            const allowedValues = new Set(column.filterOptions.map((option) => option.value));

            return normalizeListFilterValues(rule.value).some((value) => !allowedValues.has(value));
        });

        if (!ruleWithUnavailableOption) {
            return null;
        }

        return `${ruleWithUnavailableOption.fieldLabel} includes values unavailable in this ${resourceLabel}.`;
    };

    const normalizeRulesForCurrentColumns = (rules: ListFilterRule[]) =>
        rules.map((rule) => {
            const column = visibleColumns.find((candidate) => candidate.key === rule.field);
            const operatorOptions = getOperatorOptions(column?.dataType ?? rule.dataType);
            const operatorIsStillValid = operatorOptions.some((option) => option.value === rule.operator);
            const [fallbackOperator] = operatorOptions;

            return {
                ...rule,
                dataType: column?.dataType ?? rule.dataType,
                fieldLabel: column?.label ?? rule.fieldLabel,
                operator: operatorIsStillValid ? rule.operator : (fallbackOperator?.value ?? 'contains'),
                operatorLabel: operatorIsStillValid ? rule.operatorLabel : (fallbackOperator?.label ?? 'Contains'),
            };
        });

    const normalizeAdvancedFilterForCurrentColumns = (
        definition: AdvancedFilterDefinition<ListFilterRule> | undefined,
    ): AdvancedFilterDefinition<ListFilterRule> | null =>
        definition
            ? {
                  ...definition,
                  groups: definition.groups.map((group) => ({
                      ...group,
                      conditions: group.conditions.map((condition) => ({
                          ...condition,
                          rule: normalizeRulesForCurrentColumns([condition.rule])[0] ?? condition.rule,
                      })),
                  })),
              }
            : null;

    const applySavedFilter = (filter: SavedFilter<ListFilterRule>) => {
        const issue = getCompatibilityIssue(filter) ?? getUnavailableOptionIssue(filter);

        if (issue) {
            return;
        }

        const rules = normalizeRulesForCurrentColumns(filter.definition?.rules ?? []);
        const nextAdvancedFilter = normalizeAdvancedFilterForCurrentColumns(filter.definition?.advancedFilter);
        if (setFilterDefinition) {
            setFilterDefinition(rules.length ? rules : null, nextAdvancedFilter, { savedFilterId: filter.id });
        } else {
            setFilters(rules.length ? rules : null, { savedFilterId: filter.id });
        }
        setOpen(false);
    };

    const handleSave = async (values: SavedFilterFormValues) => {
        if (!resourceId) {
            return;
        }

        const commonPayload = {
            description: values.description?.trim() || null,
            isResourceDefault: values.isResourceDefault,
            name: values.name.trim(),
            resourceId,
            resourceType,
            scope: values.scope,
            targetKind,
            visibility: values.visibility,
        };

        setSaving(true);

        try {
            if (editingFilter) {
                await savedFilterService.update<ListFilterRule>(editingFilter.id, {
                    ...commonPayload,
                    ...(values.replaceDefinition ? { definition: buildDefinition() } : {}),
                });
                toast('Saved filter updated', { description: values.name.trim() });
            } else {
                await savedFilterService.create<ListFilterRule>({
                    ...commonPayload,
                    definition: buildDefinition(),
                });
                toast('Filter saved', { description: values.name.trim() });
            }

            setModalOpen(false);
            await loadSavedFilters();
        } catch (error) {
            captureException(error, { tags: { feature: 'saved-filter-save', resourceType } });
            toast.error('Unable to save filter');
        } finally {
            setSaving(false);
        }
    };

    const deleteSavedFilter = async (filter: SavedFilter<ListFilterRule>) => {
        try {
            await savedFilterService.delete(filter.id);
            toast('Saved filter deleted', { description: filter.name });
            await loadSavedFilters();
        } catch (error) {
            captureException(error, { tags: { feature: 'saved-filter-delete', resourceType } });
            toast.error('Unable to delete saved filter');
        }
    };

    const docsFooter = <LearnMoreFooter topic={docsLabel} variant='dropdown' />;

    const renderSavedFilterItem = (filter: SavedFilter<ListFilterRule>) => {
        const issue = getCompatibilityIssue(filter) ?? getUnavailableOptionIssue(filter);
        const disabled = Boolean(issue);
        const ruleCount =
            (filter.definition?.rules?.length ?? 0) +
            getAdvancedFilterCounts(filter.definition?.advancedFilter).conditions;
        const visibilityLabel =
            filter.visibility === SavedFilterVisibility.WORKSPACE ? 'Workspace' : capitalCase(filter.visibility);
        const scopeLabel = filter.scope === SavedFilterScope.WORKSPACE ? compatibleResourceLabel : resourceScopedLabel;
        const metadata = issue
            ? [<span key='issue'>{issue}</span>]
            : [
                  <span key='rules'>{`${ruleCount} ${ruleCount === 1 ? 'Filter Rule' : 'Filter Rules'}`}</span>,
                  <span key='visibility' style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                      {filter.visibility === SavedFilterVisibility.WORKSPACE ? (
                          <TbWorld size='0.75rem' />
                      ) : (
                          <TbLock size='0.75rem' />
                      )}
                      {visibilityLabel}
                  </span>,
                  <span key='scope' style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}>
                      <ListFilter size='0.6875rem' />
                      {scopeLabel}
                  </span>,
                  ...(filter.isResourceDefault
                      ? [
                            <span
                                key='default'
                                style={{ alignItems: 'center', display: 'inline-flex', gap: '0.25rem' }}
                            >
                                <TbWorld size='0.75rem' />
                                Default
                            </span>,
                        ]
                      : []),
              ];

        return (
            <Menu.Item
                aria-disabled={disabled}
                key={filter.id}
                onClick={() => applySavedFilter(filter)}
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    height: 'auto',
                    lineHeight: 1.35,
                    opacity: disabled ? 0.55 : 1,
                    paddingBlock: '0.5rem',
                }}
            >
                <DropdownOptionContent
                    avatar={{
                        icon: <ListFilter size='0.75rem' />,
                        props: { shape: 'square', size: 20 },
                    }}
                    extra={
                        filter.canEdit ? (
                            <Space.Compact size='small'>
                                <SmallTooltip title='Edit saved filter' placement='top'>
                                    <Button
                                        aria-label={`Edit saved filter ${filter.name}`}
                                        size='small'
                                        type='text'
                                        icon={<TbEdit size='0.875rem' style={{ color: token.colorTextTertiary }} />}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openEditModal(filter);
                                        }}
                                    />
                                </SmallTooltip>
                                <Popconfirm
                                    title='Delete saved filter?'
                                    description='This saved filter will no longer be available from this dropdown.'
                                    okText='Delete'
                                    okButtonProps={{ danger: true }}
                                    onCancel={(event) => event?.stopPropagation()}
                                    onConfirm={(event) => {
                                        event?.stopPropagation();
                                        deleteSavedFilter(filter);
                                    }}
                                    placement='topRight'
                                >
                                    <span onClick={(event) => event.stopPropagation()}>
                                        <SmallTooltip title='Delete saved filter' placement='top'>
                                            <Button
                                                aria-label={`Delete saved filter ${filter.name}`}
                                                danger
                                                size='small'
                                                type='text'
                                                icon={<TbTrash size='0.875rem' />}
                                            />
                                        </SmallTooltip>
                                    </span>
                                </Popconfirm>
                            </Space.Compact>
                        ) : undefined
                    }
                    metadata={metadata}
                    title={
                        <Typography.Text
                            style={{
                                color: token.colorText,
                                display: 'block',
                                fontWeight: 500,
                                ...ellipsisStyling,
                            }}
                        >
                            {filter.name}
                        </Typography.Text>
                    }
                />
            </Menu.Item>
        );
    };

    const overlay = (
        <DropdownPanel
            footer={docsFooter}
            maxHeight={16 * 20}
            search={{
                placeholder: 'Search Filters...',
                setValue: setSearch,
                value: search,
            }}
            style={{ marginTop: '0.5rem' }}
            width={16 * 26}
        >
            {loading ? (
                <div aria-label='Loading saved filters' style={{ display: 'grid', gap: '0.75rem', padding: '0.75rem' }}>
                    {[0, 1, 2].map((index) => (
                        <ListItemSkeleton
                            avatar
                            avatarShape='square'
                            avatarSize={20}
                            key={index}
                            lineWidths={index === 1 ? ['55%', '72%'] : ['45%', '68%']}
                        />
                    ))}
                </div>
            ) : filteredSavedFilters.length ? (
                <DropdownPanelMenu selectable={false}>
                    {filteredSavedFilters.map(renderSavedFilterItem)}
                </DropdownPanelMenu>
            ) : (
                <div style={{ padding: '0 0.25rem 0.25rem' }}>
                    <EmptySection
                        height='10rem'
                        icon={<ListFilter size='70%' style={{ color: token.colorTextTertiary }} />}
                        label='No Saved Filters'
                        description={`Save a filter to reuse it here or in other compatible ${compatibleResourceLabel}.`}
                    />
                </div>
            )}
        </DropdownPanel>
    );

    return (
        <>
            <Space.Compact size='small'>
                <Button
                    disabled={(!filters.length && !getAdvancedFilterCounts(advancedFilter).conditions) || !resourceId}
                    icon={<TbDeviceFloppy size='0.875rem' />}
                    onClick={openCreateModal}
                >
                    Save Filter
                </Button>
                <Dropdown
                    open={open}
                    onOpenChange={setOpen}
                    popupRender={() => overlay}
                    placement='bottomRight'
                    trigger={['click']}
                >
                    <DropdownButton disabled={!resourceId}>View Saved Filters</DropdownButton>
                </Dropdown>
            </Space.Compact>
            <Modal
                afterOpenChange={(isOpen) => {
                    if (!isOpen) {
                        return;
                    }

                    form.resetFields();
                    form.setFieldsValue(
                        editingFilter
                            ? {
                                  description: editingFilter.description ?? '',
                                  isResourceDefault: editingFilter.isResourceDefault,
                                  name: editingFilter.name,
                                  replaceDefinition: false,
                                  scope: editingFilter.scope,
                                  visibility: editingFilter.visibility,
                              }
                            : DEFAULT_SAVED_FILTER_VALUES,
                    );
                }}
                destroyOnClose
                confirmLoading={saving}
                okButtonProps={{
                    disabled: !editingFilter && !filters.length && !getAdvancedFilterCounts(advancedFilter).conditions,
                }}
                okText={editingFilter ? 'Update Filter' : 'Save Filter'}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                open={modalOpen}
                title={editingFilter ? 'Edit Saved Filter' : 'Save Filter'}
            >
                <Form<SavedFilterFormValues>
                    form={form}
                    initialValues={DEFAULT_SAVED_FILTER_VALUES}
                    layout='vertical'
                    onFinish={handleSave}
                    preserve={false}
                    requiredMark={false}
                    onValuesChange={(changedValues) => {
                        if (
                            ('scope' in changedValues || 'visibility' in changedValues) &&
                            form.getFieldValue('isResourceDefault') &&
                            (form.getFieldValue('scope') !== SavedFilterScope.RESOURCE ||
                                form.getFieldValue('visibility') !== SavedFilterVisibility.WORKSPACE)
                        ) {
                            form.setFieldValue('isResourceDefault', false);
                        }
                    }}
                >
                    <Form.Item
                        label='Name'
                        name='name'
                        rules={[{ required: true, message: 'Enter a name for this saved filter.' }]}
                        style={{ marginBottom: '0.5rem' }}
                    >
                        <Input maxLength={250} placeholder='Executive Contacts At Target Accounts...' />
                    </Form.Item>
                    <Form.Item label='Description' name='description' style={{ marginBottom: '0.75rem' }}>
                        <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            maxLength={500}
                            placeholder='Optional Context For This Saved Filter...'
                        />
                    </Form.Item>
                    <Form.Item label='Visibility' name='visibility' style={{ marginBottom: '0.75rem' }}>
                        <DropdownButtonSelect<SavedFilterVisibility>
                            options={[
                                {
                                    description: `Visible only to you in this ${resourceLabel}.`,
                                    disabled: Boolean(editingFilter && !editingFilter.canChangeVisibility),
                                    icon: <TbLock />,
                                    label: 'Only Me',
                                    value: SavedFilterVisibility.PERSONAL,
                                },
                                {
                                    description: `Available to members who can access this ${resourceLabel}.`,
                                    disabled: !canManageSharedFilters,
                                    icon: <TbWorld />,
                                    label: 'Workspace Members',
                                    value: SavedFilterVisibility.WORKSPACE,
                                },
                            ]}
                            placeholder='Choose Visibility...'
                        />
                    </Form.Item>
                    <Form.Item label='Reuse' name='scope' style={{ marginBottom: '0.75rem' }}>
                        <DropdownButtonSelect<SavedFilterScope>
                            options={[
                                {
                                    description: `Keep this filter attached to this ${resourceLabel}.`,
                                    icon: <ListFilter size='0.875rem' />,
                                    label: resourceScopedLabel,
                                    value: SavedFilterScope.RESOURCE,
                                },
                                {
                                    description: workspaceScopeDescription,
                                    icon: <TbWorld />,
                                    label: workspaceScopeTitle,
                                    value: SavedFilterScope.WORKSPACE,
                                },
                            ]}
                            placeholder='Choose Reuse Scope...'
                        />
                    </Form.Item>
                    <Form.Item hidden name='isResourceDefault' valuePropName='checked'>
                        <Switch />
                    </Form.Item>
                    <SettingsToggleRow
                        checked={selectedIsResourceDefault}
                        description='Applies when a member has not created a personal filter state. Personal choices are never overwritten.'
                        disabled={
                            !canManageSharedFilters ||
                            selectedVisibility !== SavedFilterVisibility.WORKSPACE ||
                            selectedScope !== SavedFilterScope.RESOURCE
                        }
                        label={`Use as the default filter for this ${resourceLabel}`}
                        onChange={(checked) => form.setFieldValue('isResourceDefault', checked)}
                        switchAriaLabel={`Use as the default filter for this ${resourceLabel}`}
                    />
                    {editingFilter && (
                        <>
                            <Form.Item hidden name='replaceDefinition' valuePropName='checked'>
                                <Switch />
                            </Form.Item>
                            <SettingsToggleRow
                                checked={selectedReplaceDefinition}
                                description='Updates the saved definition to match the filters currently applied in this row.'
                                divided
                                disabled={!filters.length && !getAdvancedFilterCounts(advancedFilter).conditions}
                                label='Replace saved rules with the current filter row'
                                onChange={(checked) => form.setFieldValue('replaceDefinition', checked)}
                                switchAriaLabel='Replace saved rules with the current filter row'
                            />
                        </>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default SavedFiltersDropdown;
